import { Link } from "react-router-dom";
import type { TreeMapPoint, AreaVerdeMap, UCMap } from "@/mocks/mapa";
import { estadoFitoConfig, riscoConfig, statusRevisaoConfig } from "@/mocks/cta";

interface MapDetailPanelProps {
  selectedTree: TreeMapPoint | null;
  selectedArea: AreaVerdeMap | null;
  selectedUC: UCMap | null;
  open: boolean;
  onClose: () => void;
}

export default function MapDetailPanel({ selectedTree, selectedArea, selectedUC, open, onClose }: MapDetailPanelProps) {
  const selected = selectedTree || selectedArea || selectedUC;

  if (!open || !selected) return null;

  return (
    <div className="w-[340px] bg-white border-l border-background-200/70 flex flex-col shrink-0 h-full shadow-lg z-30">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
        <span className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">
          {selectedTree ? "Detalhe da Árvore" : selectedArea ? "Área Verde" : "Unidade de Conservação"}
        </span>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors" title="Fechar detalhes">
          <i className="ri-close-line text-foreground-500 text-base"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Tree Detail */}
        {selectedTree && (
          <div className="p-4 space-y-4">
            {/* Photo */}
            <div className="relative rounded-lg overflow-hidden h-40 bg-background-100">
              <img
                src={selectedTree.fotoUrl}
                alt={`Foto de ${selectedTree.nomePopular}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 left-2 flex gap-1.5">
                <span className={`badge text-[10px] ${estadoFitoConfig[selectedTree.estadoFitossanitario]?.badge || "bg-background-100 text-foreground-500"}`}>
                  {estadoFitoConfig[selectedTree.estadoFitossanitario]?.label || selectedTree.estadoFitossanitario}
                </span>
                <span className={`badge text-[10px] ${riscoConfig[selectedTree.classeRisco]?.badge || "bg-background-100 text-foreground-500"}`}>
                  {riscoConfig[selectedTree.classeRisco]?.label || selectedTree.classeRisco}
                </span>
              </div>
            </div>

            {/* Info fields */}
            <div>
              <p className="text-xs text-foreground-500 font-mono mb-1">{selectedTree.codigoCta}</p>
              <h3 className="text-base font-bold text-foreground-900 font-heading">{selectedTree.nomePopular}</h3>
              <p className="text-xs text-foreground-500 italic">{selectedTree.nomeCientifico}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <InfoField label="Tipo" value={selectedTree.tipo} />
              <InfoField label="DAP" value={`${selectedTree.dap} cm`} />
              <InfoField label="Altura" value={`${selectedTree.altura} m`} />
              <InfoField label="Recomendação" value={selectedTree.recomendacao} />
            </div>

            <div className="space-y-1.5 pt-1 border-t border-background-200/70">
              <InfoRow icon="ri-map-pin-line" label="Localização" value={`${selectedTree.logradouro}, ${selectedTree.numero} — ${selectedTree.bairro}`} />
              <InfoRow icon="ri-gps-line" label="Coordenadas" value={`${selectedTree.latitude.toFixed(6)}, ${selectedTree.longitude.toFixed(6)}`} />
              <InfoRow icon="ri-calendar-line" label="Última vistoria" value={selectedTree.ultimaVistoria} />
              <InfoRow icon="ri-user-line" label="Técnico" value={selectedTree.tecnico} />
              <InfoRow icon="ri-folder-chart-line" label="Projeto" value={selectedTree.projeto} />
            </div>

            {/* Actions */}
            <div className="space-y-2 pt-2 border-t border-background-200/70">
              <Link
                to={`/cta/${selectedTree.id.replace("MAP-", "ARV-")}`}
                className="w-full btn-primary text-xs py-2.5 whitespace-nowrap"
              >
                <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                Ver FCA completa
              </Link>
              <div className="flex gap-2">
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-clipboard-line text-xs" aria-hidden="true"></i>
                  Nova vistoria
                </button>
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-camera-line text-xs" aria-hidden="true"></i>
                  Adicionar foto
                </button>
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-file-pdf-line text-xs" aria-hidden="true"></i>
                  PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Area Verde Detail */}
        {selectedArea && (
          <div className="p-4 space-y-4">
            <div className="w-full h-32 rounded-lg bg-accent-50 flex items-center justify-center">
              <i className="ri-landscape-line text-accent-400 text-4xl" aria-hidden="true"></i>
            </div>

            <div>
              <p className="text-xs text-foreground-500 mb-1">{selectedArea.tipo}</p>
              <h3 className="text-base font-bold text-foreground-900 font-heading">{selectedArea.nome}</h3>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <InfoField label="Área" value={`${selectedArea.area} ha`} />
              <InfoField label="Árvores dentro" value={selectedArea.arvoresDentro.toLocaleString()} />
              <InfoField label="Risco crítico" value={selectedArea.riscoCritico.toString()} />
              <InfoField label="CO₂e estimado" value={`${selectedArea.co2eEstimado} t`} />
            </div>

            <div className="space-y-1.5 pt-1 border-t border-background-200/70">
              <InfoRow icon="ri-map-pin-line" label="Bairro" value={selectedArea.bairro} />
              <InfoRow icon="ri-file-list-3-line" label="Documentos" value={`${selectedArea.documentosVinculados} vinculados`} />
              <InfoRow icon="ri-checkbox-circle-line" label="Status" value={selectedArea.status} />
            </div>

            <div className="space-y-2 pt-2 border-t border-background-200/70">
              <button className="w-full btn-primary text-xs py-2.5 whitespace-nowrap">
                <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                Ver detalhe completo
              </button>
              <div className="flex gap-2">
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-file-chart-line text-xs" aria-hidden="true"></i>
                  Gerar relatório
                </button>
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-plant-line text-xs" aria-hidden="true"></i>
                  Ver árvores
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UC Detail */}
        {selectedUC && (
          <div className="p-4 space-y-4">
            <div className="w-full h-32 rounded-lg bg-accent-600/10 flex items-center justify-center">
              <i className="ri-shield-star-line text-accent-600 text-4xl" aria-hidden="true"></i>
            </div>

            <div>
              <p className="text-xs text-foreground-500 mb-1">{selectedUC.tipo}</p>
              <h3 className="text-base font-bold text-foreground-900 font-heading">{selectedUC.nome}</h3>
              <p className="text-xs text-foreground-500 mt-0.5">{selectedUC.orgaoGestor}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <InfoField label="Área" value={`${selectedUC.area} ha`} />
              <InfoField label="Árvores dentro" value={selectedUC.arvoresDentro.toLocaleString()} />
              <InfoField label="Risco crítico" value={selectedUC.riscoCritico.toString()} />
              <InfoField label="CO₂e estimado" value={`${selectedUC.co2eEstimado} t`} />
            </div>

            <div className="space-y-1.5 pt-1 border-t border-background-200/70">
              <InfoRow icon="ri-map-pin-line" label="Bairro" value={selectedUC.bairro} />
              <InfoRow icon="ri-file-list-3-line" label="Documentos" value={`${selectedUC.documentosVinculados} vinculados`} />
              <InfoRow icon="ri-checkbox-circle-line" label="Status" value={selectedUC.status} />
            </div>

            <div className="space-y-2 pt-2 border-t border-background-200/70">
              <button className="w-full btn-primary text-xs py-2.5 whitespace-nowrap">
                <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                Ver detalhe completo
              </button>
              <div className="flex gap-2">
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-file-chart-line text-xs" aria-hidden="true"></i>
                  Gerar relatório
                </button>
                <button className="flex-1 btn-outline text-xs py-2 whitespace-nowrap">
                  <i className="ri-plant-line text-xs" aria-hidden="true"></i>
                  Ver árvores
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-background-50 rounded-md px-2.5 py-2">
      <p className="text-[10px] text-foreground-500">{label}</p>
      <p className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{value}</p>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <i className={`${icon} text-foreground-400 text-xs mt-0.5 shrink-0`} aria-hidden="true"></i>
      <div className="min-w-0">
        <p className="text-[10px] text-foreground-500">{label}</p>
        <p className="text-xs text-foreground-800">{value}</p>
      </div>
    </div>
  );
}